import { useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';

import { get } from 'api/utils';
import ENDPOINTS from 'api/utils/endpoints';
import useQueryKeys from 'api/utils/useQueryKeys';
import { useSelectedDealerId } from '@lotlinx-aws/dealer-selector';
import { userAtom } from 'store';
import { assertIsDefined } from 'util/assert';

import { GeoConfigDataSchema } from './GeoConfigSchema';

export interface fetchGeoConfigProps {
  token?: string;
  dealerId?: number;
}

export interface DefaultGeoConfig {
  latitude: number;
  longitude: number;
  radius: number;
}

export interface BaseConfigLocation extends DefaultGeoConfig {
  name: string;
}

export interface BaseGeoConfig {
  name: string;
}

export interface PostGeoConfigLocation extends BaseConfigLocation {
  id?: number;
}

export interface GeoConfigLocation extends BaseConfigLocation {
  id: number;
}

export interface PostGeoConfig extends BaseGeoConfig {
  configId: number;
  locations: PostGeoConfigLocation[];
  hasActiveCampaign?: boolean;
}

export interface GeoConfig extends BaseGeoConfig {
  configId: number;
  locations: GeoConfigLocation[];
  hasActiveCampaign?: boolean;
}

export interface GeoConfigData {
  defaults: DefaultGeoConfig;
  geoConfigs: GeoConfig[];
}

export const DEFAULT_GEO_CONFIG = {
  configId: 0.1,
  name: 'Default Radius Profile',
};

/**
 * useGeoConfig
 */
async function fetchGeoConfig({ token, dealerId }: fetchGeoConfigProps) {
  assertIsDefined(token);
  assertIsDefined(dealerId);

  const res = await get<GeoConfigData>(`${ENDPOINTS.geoConfigSettings}/${dealerId}`, {
    headers: { token },
  });

  return GeoConfigDataSchema.parse(res);
}

export default function useGeoConfig() {
  const [dealerId] = useSelectedDealerId();
  const [user] = useAtom(userAtom);
  const { geoConfigKey } = useQueryKeys();

  return useQuery<GeoConfigData, Error>({
    queryKey: geoConfigKey,
    queryFn: () => fetchGeoConfig({ token: user?.token, dealerId }),
    staleTime: 4 * 60 * 60 * 1000,
  });
}
